import * as ActionTypes from '../actions/actionTypes';
import {DATA_SETS} from '../Constants';
import {
  calculateTableData,
  convertToGraphData,
  tableMetaData
} from '../utils/behaviour_models/shared/behaviour_models_utils';
import {runParityCalcsFromConfig} from '../utils/behaviour_models/shared/parity';
import {emptyGraphMap, extractTicks} from '../utils/point_utils';
import {
  calculateDIR,
  calculateFDI,
  calculateFH,
  calculateFI,
  calculateFROS,
  calculateFROS_NS,
  calculateHO,
  calculateSD,
  calculateSFFM,
  calculateWSF,
  calculateWSO,
  calculateFFDI
} from '../utils/behaviour_models/mcarthur_mk5/calculations';
import {
  DEFAULT_BOM_DF,
  DEFAULT_FFL,
  DEFAULT_SLOPE,
  DEFAULT_WRF,
  FFDI_COLORS,
  FH_COLORS
} from '../utils/behaviour_models/mcarthur_mk5/constants';

export {DEFAULT_WRF, FFDI_COLORS, FH_COLORS, calculateFFDI};

const extractVariables = (state) => {
  return {
    slope: state.slope,
    fine_fuel_load: state.fine_fuel_load,
    wind_reduction_factor: state.wind_reduction_factor,
    use_bom_df: state.use_bom_df,
    lag_temp_rh: state.lag_temp_rh
  };
};

const VALUES_CONFIG = [
  { key: 'DIR', displayName: 'Direction of Spread', calc: calculateDIR, decPlaces: 0, arrow: 'fa-arrow-up' },
  {
    key: 'FDI', displayName: 'FDI', calc: calculateFDI, decPlaces: 1,
    colors: FFDI_COLORS,
    cellGraph: {
      min: 0.0,
      max: 100.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: 'FROS', displayName: 'FROS (m/h)', calc: calculateFROS, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 1000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  { key: 'FROS_NS', displayName: 'FROS no slope no wind (m/h)', calc: calculateFROS_NS, decPlaces: 0 },
  {
    key: 'FH', displayName: 'Flame Height (m)', calc: calculateFH, decPlaces: 1,
    colors: FH_COLORS
  },
  {
    key: 'SD', displayName: 'Spotting Distance (m)', calc: calculateSD, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 2000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: 'FI', displayName: 'Fireline Intensity (kW/m)', calc: calculateFI, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 15000.0,
      leftColor: 'rgba(248, 152, 41, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  { key: 'HO', displayName: 'Heat Output (kJ/m2)', calc: calculateHO, decPlaces: 0 },
  { key: 'SFFM', displayName: 'Surface Fine Fuel Moisture (%)', calc: calculateSFFM, decPlaces: 1 },
  { key: 'WSF', displayName: '1.5m Wind Speed in Forest (km/h)', calc: calculateWSF, decPlaces: 0 },
  { key: 'WSO', displayName: '2.0m Wind Speed in Open (km/h)', calc: calculateWSO, decPlaces: 0 }
];

const GRAPHS_CONFIG = [
  {
    title: 'Forward Rate of Spread',
    id: 'FROS',
    type: 'LineChart',
    YAxisLabel: 'm/h',
    series: [
      {
        name: 'With Slope',
        dataKey: 'FROS',
        type: 'monotone',
        stroke: '#FF5131',
        active: true
      },
      {
        name: 'No Slope, No Wind',
        dataKey: 'FROS_NS',
        type: 'monotone',
        stroke: '#008CC4',
        active: true
      }
    ]
  },
  {
    title: 'Surface Fine Fuel Moisture',
    id: 'SFFM',
    type: 'LineChart',
    YAxisLabel: '%',
    series: [
      {
        name: 'Fuel Moisture',
        dataKey: 'SFFM',
        type: 'monotone',
        stroke: '#FFB644',
        active: true
      }
    ]
  }
];

const initialState = {
  slope: DEFAULT_SLOPE,
  fine_fuel_load: DEFAULT_FFL,
  wind_reduction_factor: DEFAULT_WRF,
  use_bom_df: DEFAULT_BOM_DF,
  lag_temp_rh: 0,
  table_data: {},
  graph_data: emptyGraphMap(GRAPHS_CONFIG),
  table_meta_data: tableMetaData(VALUES_CONFIG, DATA_SETS.MCARTHUR_MK5),
  graph_meta_data: GRAPHS_CONFIG
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_POINT_TOTAL_FUEL_LOAD:
      if (action.total_fuel_load) {
        return {
          ...state,
          fine_fuel_load: parseFloat(action.total_fuel_load).toFixed(0)
        };
      }
      return {
        ...state,
        fine_fuel_load: DEFAULT_FFL
      };

    case ActionTypes.SET_BEHAVIOUR_VARIABLES:
      if (action.data_set === DATA_SETS.MCARTHUR_MK5) {
        return {
          ...state,
          ...action.variables
        };
      }
      break;
    case ActionTypes.CALCULATE_MCARTHUR_MK5:
      const variables = extractVariables(state);
      const modifications = action.modifications ? action.modifications.properties.data : {};
      const new_table_data =
        calculateTableData(VALUES_CONFIG,
          action.include_history,
          action.short_future,
          action.weather_data.table_data,
          variables,
          modifications);
      const new_graph_data = convertToGraphData(GRAPHS_CONFIG, new_table_data);
      const ticks = extractTicks(new_graph_data, GRAPHS_CONFIG[0].id);

      return {
        ...state,
        ticks,
        graph_data: new_graph_data,
        table_data: new_table_data
      };

    default:
      return state;
  }
  return state;
};

export const runParityCalcs = (wd, variables) => runParityCalcsFromConfig(VALUES_CONFIG, wd, variables);

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/point_data_mcarthur_mk5.js